import { useMutation } from "@apollo/client/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Mutations, Queries } from "@/constants";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";
import {
  type RuleDefinitionSuggestionCategorizeFragment,
  type RuleDefinitionSuggestionDuplicateFragment,
  type RuleDefinitionSuggestionMatchDocumentFragment,
  type RuleDefinitionSuggestionMatchDocumentObject,
  type RuleDefinitionSuggestionSplitFragment,
  type RuleDefinitionSuggestionTransferFragment,
  type RuleFragment,
  MatchDocumentAmountToleranceModeEnum,
  MatchDocumentResidualReasonEnum,
  RuleDefinitionSuggestionTypeEnum,
} from "@/__generated__/graphql";

import {
  Word,
  type WordOption,
} from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidget.tools";
import { ReportsViewWidgetWhereEditor } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetWhereEditor";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

import { RuleMatchDocumentAction } from "./RuleMatchDocumentAction";
import type { FilterGroup, RuleDefinitionSuggestion } from "./types/rule";
import { RuleWhenDescription } from "./RuleWhenDescription";
import { useRuleEditorStore } from "./ruleEditorStore";
import {
  RULE_WHEN_SOURCE,
  filterGroupToWhereGroup,
  ruleConditionsToFilterGroup,
  serializeFilterGroup,
  whereGroupToFilterGroup,
} from "./utils/filterGroup";

const RULE_SUGGESTIONS = [
  {
    key: "match_document",
    label: "Link bank payment to a document",
    type: RuleDefinitionSuggestionTypeEnum.MatchDocument,
    defaults: {
      directionMode: "PAYMENTS_MATCH_BILLS",
      amountTolerance: 0.01,
      amountToleranceMode: "ABSOLUTE",
      settlementBases: ["BALANCE", "BALANCE_PARTIAL"],
      dateWindowDaysBefore: 5,
      dateWindowDaysAfter: 45,
      matchDateBasis: ["ISSUED"],
      currencyMode: "SAME",
      includePaid: false,
      textMatchMode: "FUZZY",
      similarityThreshold: 0.8,
      automationMode: "SUGGEST",
      maxResults: 5,
      minConfidence: 0.5,
      postingMode: "LINE_ITEMS",
      copyLineItemTax: true,
    },
  },
  {
    key: "categorize",
    label: "Categorize",
    type: RuleDefinitionSuggestionTypeEnum.Categorize,
    defaults: {
      strategies: ["PRECEDENT"],
    },
  },
  {
    key: "duplicate",
    label: "Flag duplicate",
    type: RuleDefinitionSuggestionTypeEnum.Duplicate,
    defaults: {
      amountTolerance: 0.01,
      dateWindowDays: 1,
      sameFinancialAccount: true,
    },
  },
  {
    key: "split",
    label: "Split",
    type: RuleDefinitionSuggestionTypeEnum.Split,
    defaults: {
      splitMethod: "MANUAL",
      maxParts: 10,
    },
  },
  {
    key: "transfer",
    label: "Transfer",
    type: RuleDefinitionSuggestionTypeEnum.Transfer,
    defaults: {
      amountTolerance: 0.01,
      dateWindowDays: 2,
      detectFx: false,
    },
  },
  {
    key: "pair",
    label: "Pair",
    type: RuleDefinitionSuggestionTypeEnum.Pair,
    defaults: {
      amountTolerance: 0.01,
      dateWindowDays: 14,
      detectFx: false,
    },
  },
] as const;

type RuleSuggestionKey = (typeof RULE_SUGGESTIONS)[number]["key"];

const DEFAULT_SUGGESTION_KEY: RuleSuggestionKey =
  RULE_SUGGESTIONS[0]?.key ?? "match_document";

const SUGGESTION_TYPENAME_BY_TYPE: Record<
  RuleDefinitionSuggestionTypeEnum,
  RuleDefinitionSuggestion["__typename"]
> = {
  [RuleDefinitionSuggestionTypeEnum.MatchDocument]:
    "RuleDefinitionSuggestionMatchDocumentObject",
  [RuleDefinitionSuggestionTypeEnum.Categorize]:
    "RuleDefinitionSuggestionCategorizeObject",
  [RuleDefinitionSuggestionTypeEnum.Duplicate]:
    "RuleDefinitionSuggestionDuplicateObject",
  [RuleDefinitionSuggestionTypeEnum.Split]:
    "RuleDefinitionSuggestionSplitObject",
  [RuleDefinitionSuggestionTypeEnum.Transfer]:
    "RuleDefinitionSuggestionTransferObject",
  [RuleDefinitionSuggestionTypeEnum.Pair]:
    "RuleDefinitionSuggestionTransferObject",
};

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === "" || value === undefined) return null;
  return (
    <div className="flex justify-between pt-0.5 pb-0.5 pl-1 pr-1">
      <Typography level="body-xs" className="text-muted-foreground">
        {label}
      </Typography>
      <Typography level="body-xs" className="text-right">
        {typeof value === "boolean" ? (value ? "Yes" : "No") : value}
      </Typography>
    </div>
  );
}

const SUGGESTION_DESCRIPTIONS: Partial<Record<RuleSuggestionKey, string>> = {
  match_document:
    "Find a bill, invoice, or receipt that belongs to this bank payment.",
  categorize: "Suggest an account from merchant history or fixed mapping.",
  duplicate: "Flag another bank transaction that may be a duplicate.",
  split: "Break one bank payment across multiple accounts.",
  transfer: "Match a transfer between two financial accounts.",
  pair: "Link related bank transactions such as a fee and its payout.",
};

/** The actions a rule can take, as the word under "Then" offers them. */
const SUGGESTION_WORD_OPTIONS: WordOption[] = RULE_SUGGESTIONS.map((spec) => ({
  value: spec.key,
  label: spec.label,
  note: SUGGESTION_DESCRIPTIONS[spec.key] ?? null,
}));

export type MatchDocumentField = keyof Pick<
  RuleDefinitionSuggestionMatchDocumentObject,
  | "directionMode"
  | "documentTypes"
  | "contactFilterMode"
  | "filteredContacts"
  | "settlementEntities"
  | "includePaid"
  | "amountTolerance"
  | "amountToleranceMode"
  | "dateWindowDaysBefore"
  | "dateWindowDaysAfter"
  | "dateWindowDays"
  | "currencyMode"
  | "rateTolerance"
  | "matchDateBasis"
  | "textMatchMode"
  | "similarityThreshold"
  | "settlementBases"
  | "maxCombinedDocuments"
  | "postingMode"
  | "payableAccount"
  | "receivableAccount"
  | "fixedAccount"
  | "copyLineItemTax"
  | "uncodedLinePolicy"
  | "uncodedAccount"
  | "unsummedLinesPolicy"
  | "maxResults"
  | "residualRules"
  | "undatedDocumentPolicy"
>;

const MATCH_DOCUMENT_ACCOUNT_FIELDS = new Set<MatchDocumentField>([
  "payableAccount",
  "receivableAccount",
  "fixedAccount",
  "uncodedAccount",
]);

const MATCH_DOCUMENT_LIST_FIELDS = new Set<MatchDocumentField>([
  "filteredContacts",
  "settlementEntities",
]);

function RuleSuggestionConfigSection({
  phase,
  children,
}: {
  phase: "find" | "apply";
  children: React.ReactNode;
}) {
  return (
    <div data-testid={phase === "find" ? "rule-find-card" : "rule-apply-card"}>
      <Typography level="title-lg" className="mb-1.5">
        {phase === "find" ? "Find" : "Apply"}
      </Typography>
      <div className="flex flex-col gap-2.5">{children}</div>
    </div>
  );
}

function CategorizeConfigFields({
  suggestion,
}: {
  suggestion: RuleDefinitionSuggestionCategorizeFragment;
}) {
  return (
    <div className="flex flex-col gap-4">
      <RuleSuggestionConfigSection phase="find">
        <FieldRow
          label="Strategies"
          value={suggestion.strategies?.join(", ") || null}
        />
      </RuleSuggestionConfigSection>
      <RuleSuggestionConfigSection phase="apply">
        <FieldRow label="Account" value={suggestion.setChartedAccount?.name} />
        <FieldRow label="Category" value={suggestion.category?.name} />
      </RuleSuggestionConfigSection>
    </div>
  );
}

function DuplicateConfigFields({
  suggestion,
}: {
  suggestion: RuleDefinitionSuggestionDuplicateFragment;
}) {
  return (
    <div className="flex flex-col gap-4">
      <RuleSuggestionConfigSection phase="find">
        <FieldRow label="Amount tolerance" value={suggestion.amountTolerance} />
        <FieldRow
          label="Date window"
          value={
            suggestion.dateWindowDays != null
              ? `${suggestion.dateWindowDays} days`
              : null
          }
        />
        <FieldRow
          label="Same financial account"
          value={suggestion.sameFinancialAccount}
        />
      </RuleSuggestionConfigSection>
      <RuleSuggestionConfigSection phase="apply">
        <FieldRow label="Rationale" value={suggestion.rationale} />
      </RuleSuggestionConfigSection>
    </div>
  );
}

function SplitConfigFields({
  suggestion,
}: {
  suggestion: RuleDefinitionSuggestionSplitFragment;
}) {
  return (
    <div className="flex flex-col gap-4">
      <RuleSuggestionConfigSection phase="find">
        <FieldRow label="Split method" value={suggestion.splitMethod} />
        <FieldRow label="Max parts" value={suggestion.maxParts} />
      </RuleSuggestionConfigSection>
      <RuleSuggestionConfigSection phase="apply">
        <FieldRow label="Rationale" value={suggestion.rationale} />
      </RuleSuggestionConfigSection>
    </div>
  );
}

function TransferConfigFields({
  suggestion,
}: {
  suggestion: RuleDefinitionSuggestionTransferFragment;
}) {
  const isPair = suggestion.type === RuleDefinitionSuggestionTypeEnum.Pair;
  return (
    <div className="flex flex-col gap-4">
      <RuleSuggestionConfigSection phase="find">
        <FieldRow label="Amount tolerance" value={suggestion.amountTolerance} />
        <FieldRow
          label="Date window"
          value={
            suggestion.dateWindowDays != null
              ? `${suggestion.dateWindowDays} days`
              : null
          }
        />
        {!isPair ? (
          <FieldRow label="Detect FX" value={suggestion.detectFx} />
        ) : null}
      </RuleSuggestionConfigSection>
      <RuleSuggestionConfigSection phase="apply">
        <FieldRow
          label={isPair ? "Offset account" : "Clearing account"}
          value={suggestion.clearingAccount?.name}
        />
        <FieldRow label="Rationale" value={suggestion.rationale} />
      </RuleSuggestionConfigSection>
    </div>
  );
}

function SuggestionConfigFields({
  suggestion,
  readOnly,
  onCommitMatchDocument,
  onCommitMatchDocumentFields,
}: {
  suggestion: RuleDefinitionSuggestion;
  readOnly?: boolean;
  onCommitMatchDocument: (field: MatchDocumentField, value: unknown) => void;
  onCommitMatchDocumentFields: (
    updates: Partial<Record<MatchDocumentField, unknown>>,
  ) => void;
}) {
  switch (suggestion.__typename) {
    case "RuleDefinitionSuggestionMatchDocumentObject":
      return (
        <RuleMatchDocumentAction
          suggestion={suggestion}
          readOnly={readOnly}
          onCommit={onCommitMatchDocument}
          onCommitFields={onCommitMatchDocumentFields}
        />
      );
    case "RuleDefinitionSuggestionCategorizeObject":
      return <CategorizeConfigFields suggestion={suggestion} />;
    case "RuleDefinitionSuggestionDuplicateObject":
      return <DuplicateConfigFields suggestion={suggestion} />;
    case "RuleDefinitionSuggestionSplitObject":
      return <SplitConfigFields suggestion={suggestion} />;
    case "RuleDefinitionSuggestionTransferObject":
      return <TransferConfigFields suggestion={suggestion} />;
    default:
      return null;
  }
}

async function createRuleSuggestion(args: {
  createNodeMutate: (options: {
    variables: Record<string, unknown>;
  }) => Promise<unknown>;
  entityIdentifier: string;
  ruleIdentifier: string;
  name: string;
  type: RuleDefinitionSuggestionTypeEnum;
  fields: Record<string, unknown>;
}): Promise<{ id: string; identifier: string } | null> {
  const result: any = await args.createNodeMutate({
    variables: {
      input: {
        entityIdentifier: args.entityIdentifier,
        node: {
          ruleNode: {
            name: args.name,
            type: args.type,
            rule: { identifier: args.ruleIdentifier },
            ...args.fields,
          },
        },
      },
    },
  });
  const payload = result?.data?.createNode;
  if (payload?.errors) {
    console.error("createNode errors:", payload.errors);
  }
  const created = payload?.data?.[0];
  if (!created?.id) {
    console.error("createNode returned no id", payload);
    return null;
  }
  return {
    id: created.id as string,
    identifier: (created.identifier as string) ?? idToIdentifier(created.id)!,
  };
}

function SkeletonChip() {
  return <Skeleton variant="rectangular" className="rounded-md h-10 w-full" />;
}

function RuleEditorSkeleton() {
  return (
    <>
      <div>
        <Skeleton variant="text" level="title-lg" className="w-25 mb-3" />
        <div className="flex flex-col gap-2">
          <SkeletonChip />
          <SkeletonChip />
        </div>
      </div>

      <Separator className="my-4" />

      <div>
        <Skeleton variant="text" level="title-lg" className="w-20 mb-3" />
        <div className="flex flex-col gap-2">
          <SkeletonChip />
          <SkeletonChip />
        </div>
      </div>
    </>
  );
}

/**
 * A rule's actions as a rule not yet created holds them: each carries the
 * ``ruleNode`` input it is created with, alongside the fields shown.
 */
export type DraftRuleSuggestion = RuleDefinitionSuggestion & {
  _input: Record<string, unknown>;
};

export function RuleConditionsActionsView({
  rule,
  initialFilters,
  onPersistFilters,
  onRuleChange,
}: {
  /** A rule without an id is a draft: nothing saves until it is created. */
  rule: RuleFragment;
  /** A draft's conditions, which it has no saved rows for. */
  initialFilters?: FilterGroup;
  onPersistFilters?: (group: FilterGroup) => void;
  onRuleChange?: (updater: (r: RuleFragment) => RuleFragment) => void;
}) {
  const readOnly = !onRuleChange && !onPersistFilters;
  const isDraft = !rule.id;
  const ensureSuggestionRef = useRef<string | null>(null);
  const [createNodeMutate] = useMutation(Mutations.CREATE_NODE);
  const [deleteNodesMutate] = useMutation(Mutations.DELETE_NODES);
  const [updateNodeMutate] = useMutation(Mutations.UPDATE_NODE);
  const serverFilters = useMemo(
    () => initialFilters ?? ruleConditionsToFilterGroup(rule.conditions ?? []),
    [initialFilters, rule.conditions],
  );
  const [filters, setFilters] = useState(serverFilters);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const latestFiltersRef = useRef(filters);
  latestFiltersRef.current = filters;

  // Only hydrate from the server when switching rules — not on every
  // mutation response. Immediate persist on each chip edit races with
  // in-flight saves and was resetting operator/value before the picker
  // could open (e.g. reference fields stuck at "is…").
  // A draft's conditions can arrive after it mounts, as the form fills in.
  useEffect(() => {
    setFilters(serverFilters);
  }, [rule.id, initialFilters]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const schedulePersistFilters = useCallback(
    (nextGroup: FilterGroup) => {
      if (!onPersistFilters) return;
      // A draft only holds them, so it has nothing to wait for.
      if (isDraft) {
        onPersistFilters(nextGroup);
        return;
      }
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        onPersistFilters(latestFiltersRef.current);
      }, 500);
    },
    [isDraft, onPersistFilters],
  );

  const suggestions = useMemo(() => rule.suggestions ?? [], [rule.suggestions]);
  // The kinds the rule does not do yet: each, a way to add one.
  const addable = SUGGESTION_WORD_OPTIONS.filter(
    (option) =>
      !suggestions.some(
        (suggestion) =>
          suggestion.type ===
          RULE_SUGGESTIONS.find((spec) => spec.key === option.value)?.type,
      ),
  );

  const addSuggestion = useCallback(
    async (choice: RuleSuggestionKey) => {
      if (!onRuleChange) return;
      const spec = RULE_SUGGESTIONS.find((a) => a.key === choice);
      if (!spec) return;
      if (isDraft) {
        onRuleChange((r) => ({
          ...r,
          suggestions: [
            ...r.suggestions,
            {
              __typename: SUGGESTION_TYPENAME_BY_TYPE[spec.type],
              id: `draft-${spec.key}`,
              identifier: "",
              name: spec.label,
              type: spec.type,
              ...spec.defaults,
              _input: { ...spec.defaults },
            } as unknown as DraftRuleSuggestion,
          ],
        }));
        return;
      }
      const entityIdentifier = State.getState().getEntityIdentifier();
      const ruleIdentifier = idToIdentifier(rule.id);
      if (!entityIdentifier || !ruleIdentifier) {
        console.error("Missing entity or rule identifier");
        return;
      }

      try {
        const created = await createRuleSuggestion({
          createNodeMutate,
          entityIdentifier,
          ruleIdentifier,
          name: spec.label,
          type: spec.type,
          fields: { ...spec.defaults },
        });
        if (!created) return;

        onRuleChange((r) => ({
          ...r,
          suggestions: [
            ...r.suggestions,
            {
              __typename: SUGGESTION_TYPENAME_BY_TYPE[spec.type],
              id: created.id,
              identifier: created.identifier,
              ...spec.defaults,
            } as RuleDefinitionSuggestion,
          ],
        }));
      } catch (err) {
        console.error("Failed to add suggestion:", err);
      }
    },
    [createNodeMutate, isDraft, onRuleChange, rule.id],
  );

  // A rule that does nothing yet starts with the first kind, to be changed.
  useEffect(() => {
    if (readOnly || !onRuleChange || suggestions.length > 0) return;
    const once = rule.id || "draft";
    if (ensureSuggestionRef.current === once) return;
    ensureSuggestionRef.current = once;
    void addSuggestion(DEFAULT_SUGGESTION_KEY);
  }, [addSuggestion, onRuleChange, readOnly, rule.id, suggestions.length]);

  // One of what it does, taken off: deleted, or - a draft's - left out of it.
  const removeSuggestion = async (suggestion: RuleDefinitionSuggestion) => {
    if (!onRuleChange) return;
    if (!isDraft) {
      try {
        await deleteNodesMutate({
          variables: { input: { ids: [suggestion.id] } },
        });
      } catch (err) {
        console.error("Failed to remove suggestion:", err);
        return;
      }
    }
    onRuleChange((r) => ({
      ...r,
      suggestions: r.suggestions.filter((one) => one.id !== suggestion.id),
    }));
  };

  const commitMatchDocumentField = async (
    suggestion: RuleDefinitionSuggestion,
    field: MatchDocumentField,
    value: unknown,
  ) => {
    await commitMatchDocumentFields(suggestion, { [field]: value });
  };

  const commitMatchDocumentFields = async (
    suggestion: RuleDefinitionSuggestion,
    updates: Partial<Record<MatchDocumentField, unknown>>,
  ) => {
    if (!onRuleChange || suggestion.id.startsWith("suggestion-")) return;

    const optimisticUpdates: Partial<RuleDefinitionSuggestionMatchDocumentFragment> =
      {};
    const mutationUpdates: Record<string, unknown> = {};

    for (const [field, value] of Object.entries(updates) as Array<
      [MatchDocumentField, unknown]
    >) {
      let optimisticValue: unknown = value;
      let mutationValue: unknown = value;

      if (MATCH_DOCUMENT_ACCOUNT_FIELDS.has(field)) {
        if (value == null) {
          optimisticValue = null;
          mutationValue = null;
        } else if (
          typeof value === "object" &&
          value !== null &&
          "identifier" in value
        ) {
          optimisticValue = value;
          mutationValue = {
            identifier: (value as { identifier: string }).identifier,
          };
        }
      } else if (MATCH_DOCUMENT_LIST_FIELDS.has(field)) {
        if (Array.isArray(value)) {
          optimisticValue = value;
          mutationValue = {
            nodes: value.map((item) => ({
              identifier: typeof item === "string" ? item : item.identifier,
            })),
          };
        } else {
          optimisticValue = [];
          mutationValue = { nodes: [] };
        }
      } else if (
        (field === "documentTypes" ||
          field === "settlementBases" ||
          field === "matchDateBasis") &&
        Array.isArray(value)
      ) {
        optimisticValue = value;
        mutationValue = value;
      } else if (field === "residualRules") {
        type ResidualRuleRow = {
          reason: string;
          tolerance?: number | null;
          percentTolerance?: number | null;
          chartedAccount?: { identifier?: string } | null;
        };
        const rows = Array.isArray(value) ? (value as ResidualRuleRow[]) : [];
        optimisticValue = rows;
        mutationValue = rows.map((row) => ({
          reason: row.reason,
          tolerance: String(row.tolerance ?? 0),
          percentTolerance: String(row.percentTolerance ?? 0),
          ...(row.chartedAccount?.identifier
            ? { chartedAccount: row.chartedAccount.identifier }
            : {}),
        }));
      }

      optimisticUpdates[
        field as keyof RuleDefinitionSuggestionMatchDocumentFragment
      ] = optimisticValue as never;
      mutationUpdates[field] = mutationValue;
    }

    const matchSuggestion =
      suggestion as RuleDefinitionSuggestionMatchDocumentFragment;
    if (
      ("amountTolerance" in updates || "amountToleranceMode" in updates) &&
      !("residualRules" in updates)
    ) {
      const writeOffRule = matchSuggestion.residualRules?.find(
        (rule) => rule.reason === MatchDocumentResidualReasonEnum.WriteOff,
      );
      if (writeOffRule?.chartedAccount?.identifier) {
        const nextTolerance =
          "amountTolerance" in updates
            ? updates.amountTolerance
            : matchSuggestion.amountTolerance;
        const nextMode =
          "amountToleranceMode" in updates
            ? updates.amountToleranceMode
            : matchSuggestion.amountToleranceMode;
        const toleranceNum =
          nextTolerance == null || nextTolerance === ""
            ? 0.01
            : Number(nextTolerance);
        const isPercent =
          nextMode === MatchDocumentAmountToleranceModeEnum.Percent;
        const syncedRows = [
          {
            reason: MatchDocumentResidualReasonEnum.WriteOff,
            tolerance: isPercent ? 0 : toleranceNum,
            percentTolerance: isPercent ? toleranceNum : 0,
            chartedAccount: writeOffRule.chartedAccount,
          },
        ];
        optimisticUpdates.residualRules = syncedRows as never;
        mutationUpdates.residualRules = syncedRows.map((row) => ({
          reason: row.reason,
          tolerance: String(row.tolerance ?? 0),
          percentTolerance: String(row.percentTolerance ?? 0),
          chartedAccount: row.chartedAccount.identifier,
        }));
      }
    }

    onRuleChange((r) => ({
      ...r,
      suggestions: r.suggestions.map((s) =>
        s.id === suggestion.id
          ? ({
              ...s,
              ...optimisticUpdates,
              ...(isDraft && {
                _input: {
                  ...(s as DraftRuleSuggestion)._input,
                  ...mutationUpdates,
                },
              }),
            } as RuleDefinitionSuggestion)
          : s,
      ),
    }));
    if (isDraft) return;
    try {
      await updateNodeMutate({
        variables: {
          input: {
            id: suggestion.id,
            node: { ruleNode: { actionMatchDocument: mutationUpdates } },
          },
        },
        // The reply names the suggestion but not its settings: the rule is read
        // again so the cache holds what was saved, and a later read of it says
        // what was chosen rather than what the page loaded with.
        refetchQueries: [Queries.RULE_QUERY],
      });
    } catch (err) {
      console.error("Failed to update suggestion:", err);
    }
  };

  return (
    <div className="flex flex-col gap-4 @container">
      <div data-testid="rule-when-card">
        <Typography level="title-lg" className="mb-1.5">
          When
        </Typography>
        {/* Written as the report widgets' where is: the same sentence
                builder, over what a new transaction is matched on - it reads
                as the rule does. Read-only, the rule is said in prose. */}
        {readOnly ? (
          <RuleWhenDescription
            rule={rule}
            filterGroup={filters}
            level="body-sm"
            className="text-muted-foreground"
          />
        ) : (
          <ReportsViewWidgetWhereEditor
            source={RULE_WHEN_SOURCE}
            group={filterGroupToWhereGroup(filters)}
            recordsBy="identifier"
            // The "When" heading opens the sentence.
            leadWord={null}
            onChange={(where) => {
              const next = whereGroupToFilterGroup(where);
              setFilters(next);
              latestFiltersRef.current = next;
              schedulePersistFilters(next);
            }}
          />
        )}
      </div>

      <div data-testid="rule-suggestion-type">
        <Typography level="title-lg" className="mb-1.5">
          Then
        </Typography>
      </div>

      {/* Every kind the rule suggests, each with its own settings - a rule may
          do several, as a business's default one does. */}
      {suggestions.map((suggestion, at) => {
        const spec = RULE_SUGGESTIONS.find(
          (one) => one.type === suggestion.type,
        );
        return (
          <div
            key={suggestion.id}
            data-testid={`rule-action-${suggestion.id}`}
            className="flex flex-col"
          >
            <div className="flex items-center gap-2">
              <Typography
                level="body-md"
                data-testid="rule-suggestion-kind"
                className="[font-weight:600]"
              >
                {at > 0 ? "And " : ""}
                {at > 0
                  ? (spec?.label ?? suggestion.type).toLowerCase()
                  : (spec?.label ?? suggestion.type)}
              </Typography>
              {!readOnly && onRuleChange && suggestions.length > 1 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`rule-suggestion-remove-${suggestion.id}`}
                  onClick={() => void removeSuggestion(suggestion)}
                  className="ml-auto"
                >
                  Remove
                </Button>
              ) : null}
            </div>
            <SuggestionConfigFields
              suggestion={suggestion}
              readOnly={readOnly || !onRuleChange}
              onCommitMatchDocument={(field, value) =>
                void commitMatchDocumentField(suggestion, field, value)
              }
              onCommitMatchDocumentFields={(updates) =>
                void commitMatchDocumentFields(suggestion, updates)
              }
            />
          </div>
        );
      })}

      {/* Another kind for it to suggest besides. */}
      {!readOnly && onRuleChange && addable.length > 0 ? (
        <div data-testid="rule-suggestion-add">
          <Word
            value=""
            options={addable}
            isMidSentence={false}
            anchorLabel="+ Suggestion"
            onChange={(next) => void addSuggestion(next as RuleSuggestionKey)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function RuleEditor() {
  const rule = useRuleEditorStore((s) => s.rule);
  const updateRule = useRuleEditorStore((s) => s.updateRule);
  const [updateNodeMutate] = useMutation(Mutations.UPDATE_NODE);

  const persistFilters = useCallback(
    async (nextGroup: FilterGroup) => {
      if (!rule) return;
      const entityIdentifier = State.getState().getEntityIdentifier();
      if (!entityIdentifier) return;

      try {
        const result: any = await updateNodeMutate({
          variables: {
            input: {
              id: rule.id,
              node: {
                rule: {
                  conditions: serializeFilterGroup(nextGroup),
                },
              },
            },
          },
        });
        const updated = result?.data?.updateNode?.data?.[0];
        if (
          updated?.__typename === "RuleDefinitionObject" &&
          updated.conditions
        ) {
          updateRule((r) => ({ ...r, conditions: updated.conditions }));
        }
      } catch (err) {
        console.error("Failed to persist rule conditions:", err);
      }
    },
    [rule, updateNodeMutate, updateRule],
  );

  return (
    <div
      className="w-full h-full overflow-auto p-4 md:p-6"
      data-testid="rule-editor"
    >
      <div className="max-w-180 ml-auto mr-auto [border-width:1px] [border-style:solid] border-border rounded-lg bg-card p-5">
        {rule ? (
          <RuleConditionsActionsView
            rule={rule}
            onPersistFilters={persistFilters}
            onRuleChange={updateRule}
          />
        ) : (
          <RuleEditorSkeleton />
        )}
      </div>
    </div>
  );
}
